import React, { useEffect, useState, useCallback } from "react";
import {
  Paper,
  makeStyles,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Chip,
  Divider
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { toast } from "react-toastify";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), margin: theme.spacing(2) },
  pipelineCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: "4px solid #1976d2"
  },
  pipelineHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  stages: { marginTop: theme.spacing(1), display: "flex", flexWrap: "wrap", gap: theme.spacing(1) },
  stage: {
    padding: theme.spacing(1, 1.5),
    border: "1px solid #ddd",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    background: "#fafafa"
  }
}));

const emptyP = { name: "", description: "", active: true, isDefault: false, order: 0 };
const emptyS = { name: "", order: 0, probability: 50, color: "#1976d2", type: "open" };

const Pipelines = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [pipelines, setPipelines] = useState([]);
  const [pDlg, setPDlg] = useState({ open: false, form: emptyP, editing: null });
  const [sDlg, setSDlg] = useState({ open: false, pipelineId: null, form: emptyS, editing: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pipelines");
      setPipelines(data || []);
    } catch {
      toast.error("Erro ao carregar pipelines");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // pipeline
  const openP = (p) => setPDlg({ open: true, form: p ? { ...emptyP, ...p } : emptyP, editing: p });
  const closeP = () => setPDlg((d) => ({ ...d, open: false }));
  const saveP = async () => {
    try {
      if (pDlg.editing) await api.put(`/pipelines/${pDlg.editing.id}`, pDlg.form);
      else await api.post("/pipelines", pDlg.form);
      toast.success("Salvo");
      closeP();
      load();
    } catch (e) { toast.error(e?.response?.data?.error || "Erro"); }
  };
  const deleteP = async (p) => {
    if (!(await confirm({ title: `Excluir o pipeline "${p.name}"?`, message: "As etapas são removidas junto. Negócios que estavam nele ficam sem funil." }))) return;
    await api.delete(`/pipelines/${p.id}`);
    load();
  };

  // stage
  const openS = (pid, st) => setSDlg({ open: true, pipelineId: pid, form: st ? { ...emptyS, ...st } : emptyS, editing: st });
  const closeS = () => setSDlg((d) => ({ ...d, open: false }));
  const saveS = async () => {
    try {
      if (sDlg.editing) await api.put(`/pipelines/${sDlg.pipelineId}/stages/${sDlg.editing.id}`, sDlg.form);
      else await api.post(`/pipelines/${sDlg.pipelineId}/stages`, sDlg.form);
      toast.success("Estágio salvo");
      closeS();
      load();
    } catch (e) { toast.error(e?.response?.data?.error || "Erro"); }
  };
  const deleteS = async (pid, sid) => {
    if (!(await confirm({ title: "Excluir esta etapa?", message: "Negócios nesta etapa ficam sem posição no funil e somem da previsão até serem movidos." }))) return;
    await api.delete(`/pipelines/${pid}/stages/${sid}`);
    load();
  };

  const setPF = (patch) => setPDlg((d) => ({ ...d, form: { ...d.form, ...patch } }));
  const setSF = (patch) => setSDlg((d) => ({ ...d, form: { ...d.form, ...patch } }));

  return (
    <Paper className={classes.root}>
      {confirmDialog}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5">Pipelines de vendas</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => openP(null)}>Novo pipeline</Button>
      </div>

      {pipelines.map((p) => (
        <Paper key={p.id} className={classes.pipelineCard} variant="outlined">
          <div className={classes.pipelineHeader}>
            <div>
              <Typography variant="h6">
                {p.name} {p.isDefault && <Chip size="small" color="primary" label="Padrão" />} {!p.active && <Chip size="small" label="Inativo" />}
              </Typography>
              {p.description && <Typography variant="body2" color="textSecondary">{p.description}</Typography>}
            </div>
            <div>
              <IconButton size="small" onClick={() => openP(p)}><EditIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => deleteP(p)}><DeleteIcon fontSize="small" /></IconButton>
            </div>
          </div>
          <Divider style={{ margin: "8px 0" }} />
          <div className={classes.stages}>
            {(p.stages || []).map((s) => (
              <div key={s.id} className={classes.stage} style={{ borderLeft: `4px solid ${s.color || "#999"}` }}>
                <span>{s.name}</span>
                <Chip size="small" label={`${s.probability}%`} />
                <Chip size="small" label={s.type} />
                <IconButton size="small" onClick={() => openS(p.id, s)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => deleteS(p.id, s.id)}><DeleteIcon fontSize="small" /></IconButton>
              </div>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => openS(p.id, null)}>Estágio</Button>
          </div>
        </Paper>
      ))}

      <Dialog open={pDlg.open} onClose={closeP} maxWidth="sm" fullWidth>
        <DialogTitle>{pDlg.editing ? "Editar pipeline" : "Novo pipeline"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}><TextField label="Nome *" fullWidth value={pDlg.form.name} onChange={(e) => setPF({ name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Ordem" type="number" fullWidth value={pDlg.form.order} onChange={(e) => setPF({ order: +e.target.value })} /></Grid>
            <Grid item xs={12}><TextField label="Descrição" fullWidth value={pDlg.form.description || ""} onChange={(e) => setPF({ description: e.target.value })} /></Grid>
            <Grid item xs={6}><FormControlLabel control={<Checkbox checked={!!pDlg.form.active} onChange={(e) => setPF({ active: e.target.checked })} />} label="Ativo" /></Grid>
            <Grid item xs={6}><FormControlLabel control={<Checkbox checked={!!pDlg.form.isDefault} onChange={(e) => setPF({ isDefault: e.target.checked })} />} label="Padrão" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeP}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={saveP}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sDlg.open} onClose={closeS} maxWidth="sm" fullWidth>
        <DialogTitle>{sDlg.editing ? "Editar estágio" : "Novo estágio"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}><TextField label="Nome *" fullWidth value={sDlg.form.name} onChange={(e) => setSF({ name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Ordem" type="number" fullWidth value={sDlg.form.order} onChange={(e) => setSF({ order: +e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Probabilidade %" type="number" fullWidth value={sDlg.form.probability} onChange={(e) => setSF({ probability: +e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Tipo" fullWidth value={sDlg.form.type} onChange={(e) => setSF({ type: e.target.value })}>
                <MenuItem value="open">Aberto</MenuItem>
                <MenuItem value="won">Ganho</MenuItem>
                <MenuItem value="lost">Perdido</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}><TextField label="Cor" type="color" fullWidth InputLabelProps={{ shrink: true }} value={sDlg.form.color || "#1976d2"} onChange={(e) => setSF({ color: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeS}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={saveS}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Pipelines;
